import React from 'react';
import { useApp } from '../context/AppContext';
import { Users, FileText, DollarSign, TrendingUp, Clock, CreditCard, ArrowUpCircle } from 'lucide-react';

const Dashboard = () => {
  const { state } = useApp();
  const { customers, invoices, transactions } = state;

  const totalCustomers = customers.length;
  const totalInvoices = invoices.length;
  const totalRevenue = invoices.reduce((sum, invoice) => sum + parseFloat(invoice.subtotal || 0), 0);
  const pendingAmount = invoices.reduce((sum, invoice) => sum + parseFloat(invoice.remainingAmount || 0), 0);
  
  const totalTransactions = transactions.length;
  const totalDeposits = transactions
    .filter(t => t.transactionType === 'Deposit')
    .reduce((sum, t) => sum + parseFloat(t.amount), 0);
  const totalWithdrawals = transactions
    .filter(t => t.transactionType === 'Withdraw')
    .reduce((sum, t) => sum + parseFloat(t.amount), 0);

  const stats = [
    {
      title: 'Total Customers',
      value: totalCustomers,
      icon: Users,
      color: 'bg-gradient-to-r from-blue-500 to-blue-600',
      textColor: 'text-blue-600'
    },
    {
      title: 'Total Invoices',
      value: totalInvoices,
      icon: FileText,
      color: 'bg-gradient-to-r from-green-500 to-green-600',
      textColor: 'text-green-600'
    },
    {
      title: 'Total Revenue',
      value: `₹${totalRevenue.toLocaleString()}`,
      icon: TrendingUp,
      color: 'bg-gradient-to-r from-purple-500 to-purple-600',
      textColor: 'text-purple-600'
    },
    {
      title: 'Pending Amount',
      value: `₹${pendingAmount.toLocaleString()}`,
      icon: Clock,
      color: 'bg-gradient-to-r from-orange-500 to-orange-600',
      textColor: 'text-orange-600'
    },
    {
      title: 'Total Transactions',
      value: totalTransactions,
      icon: CreditCard,
      color: 'bg-gradient-to-r from-cyan-500 to-cyan-600',
      textColor: 'text-cyan-600'
    },
    {
      title: 'Total Deposits',
      value: `₹${totalDeposits.toLocaleString()}`,
      icon: ArrowUpCircle,
      color: 'bg-gradient-to-r from-emerald-500 to-emerald-600',
      textColor: 'text-emerald-600'
    }
  ];

  const recentCustomers = customers.slice(-5).reverse();
  const recentInvoices = invoices.slice(-5).reverse();

  return (
    <div className="p-8 space-y-8">
      <div className="text-center">
        <h1 className="text-4xl font-bold gradient-text mb-2">Dashboard</h1>
        <p className="text-gray-600 text-lg">Overview of your business performance</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <div key={index} className="stat-card group">
              <div className="flex items-center">
                <div className={`p-4 rounded-2xl ${stat.color} shadow-lg group-hover:scale-110 transition-transform duration-200`}>
                  <IconComponent className="w-8 h-8 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">{stat.title}</p>
                  <p className={`text-3xl font-bold ${stat.textColor} mt-1`}>{stat.value}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Customers */}
        <div className="glass-card">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mr-3">
              <Users className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Recent Customers</h2>
          </div>
          {recentCustomers.length > 0 ? (
            <div className="space-y-4">
              {recentCustomers.map((customer) => (
                <div key={customer.id} className="flex items-center p-4 bg-white/50 rounded-xl hover:bg-white/80 transition-all duration-200 hover:scale-105">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-lg">
                      {customer.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="ml-4">
                    <p className="font-semibold text-gray-900">{customer.name}</p>
                    <p className="text-sm text-gray-600">{customer.mobile}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 text-lg">No customers yet</p>
            </div>
          )}
        </div>

        {/* Recent Invoices */}
        <div className="glass-card">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center mr-3">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Recent Invoices</h2>
          </div>
          {recentInvoices.length > 0 ? (
            <div className="space-y-4">
              {recentInvoices.map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between p-4 bg-white/50 rounded-xl hover:bg-white/80 transition-all duration-200 hover:scale-105">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">
                        {invoice.customerName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{invoice.customerName}</p>
                      <p className="text-sm text-gray-600">
                        {new Date(invoice.date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900 text-lg">₹{(parseFloat(invoice.subtotal) || 0).toLocaleString()}</p>
                    {(parseFloat(invoice.remainingAmount) || 0) > 0 && (
                      <p className="text-sm text-red-600 font-medium">₹{(parseFloat(invoice.remainingAmount) || 0).toLocaleString()} pending</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 text-lg">No invoices yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
